import type {ReactElement} from 'react';
import {useEffect, useMemo, useState} from 'react';
import {isObjectEmpty} from '@Utils/object.util';
import {ListItemType1} from '@Components/list/components/list-item-type-1';
import {ListItemType2} from '@Components/list/components/list-item-type-2';
import {ListItemType3} from '@Components/list/components/list-item-type-3';
import {ListItemType4} from '@Components/list/components/list-item-type-4';
import {ListHeadingItem} from '@Components/list/components/list-heading-item';
import type {ListItemTypeButtonStorage} from '@Components/list/components/list-item-type-button';
import {ListItemTypeButton} from '@Components/list/components/list-item-type-button';
import {ListItemType8} from '@Components/list/components/list-item-type-8';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {ListItemStorage} from './list.types';
import {LIST_ITEM_TYPE} from './list.types';
import type {InitializeListActionProps, InitializeListItemsActionProps} from './list-reducer';
import {doesListExistInStore, getListItemsId, getNumberOfSelectedListItems, initializeListItemsState, initializeListState, reorderListItems} from './list-reducer';
import {ListItemType5} from './components/list-item-type-5';
import {type Active, defaultDropAnimationSideEffects, DndContext, DragOverlay, type DropAnimation, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {SortableContext} from '@dnd-kit/sortable';

interface ListProps {
  /**
   * Please note that the ids for lists have to be unique across all lists that are being used
   */
  id: string;
  draggable?: boolean;
  /**
   * Please note that the ids for list items have to be unique across all list items that are being used
   */
  items: ListItemStorage[];
  className?: string;
  multiSelect?: boolean;
  keepAtleastOneItemSelected?: boolean;
}

export function List({className = '', draggable = false, multiSelect = true, keepAtleastOneItemSelected = false, ...props}: ListProps): ReactElement<any> {
  const dispatch = useAppDispatch();
  const listItemsIdSelector = useMemo(() => {
    return getListItemsId;
  }, []);
  const listItemData = useAppSelector((state) => {
    return state.list.listItemData.listItemsHashMap;
  });
  const listItems = useAppSelector((state) => {
    return listItemsIdSelector(state, props.id);
  });
  const doesListExist = useAppSelector((state) => {
    return doesListExistInStore(state.list, props.id);
  });
  const getNumSelectedItems = useAppSelector((state) => {
    return getNumberOfSelectedListItems(state.list, props.id);
  });
  const [active, setActive] = useState<Active | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );
  const activeItem = useMemo(() => listItems.find((item) => item === active?.id), [active, listItems]);
  const dropAnimationConfig: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0',
        },
      },
    }),
  };

  useEffect(() => {
    initializeListItems();
    initializeList();
    checkForErrors();
  }, []);

  const checkForErrors = (): void => {
    if (doesListExist) {
      if (getNumSelectedItems === 0 && keepAtleastOneItemSelected) {
        throw new Error('keepAtleastOneItemSelected is set to true, but number of selected items passed initially as props are 0');
      }
      if (multiSelect === false && getNumSelectedItems > 1) {
        throw new Error('Multi Select is set to false, but number of selected items passed initially as props are greater than 0');
      }
    }
  };

  const initializeList = (): void => {
    const listItemsIds: string[] = [];

    props.items.forEach((itemName: ListItemStorage) => {
      listItemsIds.push(itemName.id);
    });

    const initializeListStorageObject: InitializeListActionProps = {
      listId: props.id,
      draggable,
      listItemIds: listItemsIds,
      multiSelect,
      keepAtleastOneItemSelected,
    };

    dispatch(initializeListState(initializeListStorageObject));
  };

  const initializeListItems = (): void => {
    const intializeItemStorageObject: InitializeListItemsActionProps = {
      data: props.items,
    };
    dispatch(initializeListItemsState(intializeItemStorageObject));
  };

  const onDragEndUpdated = (sourceIndex: number, destinationIndex: number): void => {
    dispatch(
      reorderListItems({
        listId: props.id,
        sourceIndex,
        destinationIndex,
      })
    );
  };

  const prepListItems = (): ReactElement<any>[] => {
    const itemsToDisplay: ReactElement<any>[] = [];
    if (listItems.length === 0 || isObjectEmpty(listItemData)) {
      return itemsToDisplay;
    }

    listItems.map((itemId) => {
      const listItem: ListItemStorage = listItemData[itemId];
      if (!listItem) {
        return;
      }
      const commonProps = {
        id: listItem.id,
        listId: props.id,
      };
      const itemFromProps: ListItemTypeButtonStorage | undefined = props.items.find((item) => {
        return item.id === listItem.id;
      }) as ListItemTypeButtonStorage;

      switch (listItem.type) {
        case LIST_ITEM_TYPE.DEFAULT_1:
          itemsToDisplay.push(<ListItemType1 key={listItem.id} {...commonProps} isDraggable={draggable} />);
          break;
        case LIST_ITEM_TYPE.DEFAULT_2:
          itemsToDisplay.push(<ListItemType2 key={listItem.id} {...commonProps} />);
          break;
        case LIST_ITEM_TYPE.DEFAULT_3:
          itemsToDisplay.push(<ListItemType3 key={listItem.id} {...commonProps} />);
          break;
        case LIST_ITEM_TYPE.DEFAULT_4:
          itemsToDisplay.push(<ListItemType4 key={listItem.id} {...commonProps} />);
          break;
        case LIST_ITEM_TYPE.DEFAULT_5:
          itemsToDisplay.push(<ListItemType5 key={listItem.id} {...commonProps} />);
          break;
        case LIST_ITEM_TYPE.DEFAULT_8:
          itemsToDisplay.push(<ListItemType8 key={listItem.id} {...commonProps} />);
          break;
        case LIST_ITEM_TYPE.HEADING:
          itemsToDisplay.push(<ListHeadingItem key={listItem.id} {...commonProps} />);
          break;
        case LIST_ITEM_TYPE.BUTTON:
          itemsToDisplay.push(<ListItemTypeButton key={listItem.id} {...commonProps} onClick={itemFromProps?.buttonProps?.onClick} />);
          break;
        default:
          break;
      }
    });
    return itemsToDisplay;
  };

  const displayDraggableListUpdated = (): ReactElement<any> => {
    return (
      <DndContext
        sensors={sensors}
        onDragStart={({active}): void => {
          setActive(active);
        }}
        onDragEnd={({active, over}): void => {
          if (!over) {
            return;
          }

          if (active.id === over.id) {
            return;
          }

          const sourceId = active.id;
          const destinationId = over.id;

          onDragEndUpdated(listItems.indexOf(String(sourceId)), listItems.indexOf(String(destinationId)));
        }}
        onDragCancel={(): void => {
          setActive(null);
        }}
      >
        <SortableContext items={listItems}>{displayList()}</SortableContext>
        <DragOverlay dropAnimation={dropAnimationConfig}>
          {activeItem ? <ListItemType1 listId={props.id} key={activeItem} id={activeItem} isDraggable={draggable} /> : null}
        </DragOverlay>
      </DndContext>
    );
  };

  const displayList = (): ReactElement<any> => {
    return (
      <ul key={props.id} className={className}>
        {prepListItems()}
      </ul>
    );
  };

  return draggable ? displayDraggableListUpdated() : displayList();
}
